import { Box, Card, CardContent, Chip, Divider, Stack, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from "@mui/material";
import { School, ArrowForward } from "@mui/icons-material";
import { Link } from "react-router-dom";
import { useState } from "react";
import { useGetStudentsQuery } from "../../services/students.service";
import { useGetSubmissionsQuery } from "../../services/submissions.service";
import { useAppTheme } from "../../context/ThemeContext";

type ViewMode = "universities" | "programs";

function TopUniversitiesWidget() {
  const { data: students } = useGetStudentsQuery("Students");
  const { data: submissions } = useGetSubmissionsQuery("Submissions");
  const { isNewTheme, accentColor } = useAppTheme();
  const [mode, setMode] = useState<ViewMode>("universities");

  const buildRanking = (items: { key: string }[]) => {
    const counts = items.reduce<Record<string, number>>((acc, { key }) => {
      if (key) acc[key] = (acc[key] ?? 0) + 1;
      return acc;
    }, {});
    return Object.entries(counts)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 8);
  };

  const ranking =
    mode === "universities"
      ? buildRanking([
          ...(students ?? []).map((s) => ({ key: (s as any).university ?? "" })),
          ...(submissions ?? []).map((s) => ({ key: "" })), // submissions don't have university
        ].filter((x) => x.key))
      : buildRanking([
          ...(students ?? []).map((s) => ({ key: (s as any).program ?? "" })),
          ...(submissions ?? []).map((s) => ({ key: (s as any).program ?? "" })),
        ].filter((x) => x.key));

  const maxCount = Math.max(...ranking.map(([, c]) => c), 1);
  const totalItems = ranking.reduce((sum, [, c]) => sum + c, 0);

  const BAR_COLORS = [accentColor, "#0EA5E9", "#8B5CF6", "#F59E0B", "#EC4899", "#F97316", "#10B981", "#64748B"];

  const cardSx = isNewTheme
    ? {
        width: "100%",
        borderRadius: "8px",
        border: "1px solid #E2E8F0",
        boxShadow: "0 2px 6px rgba(15,23,42,0.05)",
        transition: "box-shadow 0.15s ease",
        "&:hover": { boxShadow: "0 4px 16px rgba(15,23,42,0.07)" },
      }
    : { width: "100%", borderRadius: "10px", boxShadow: "0px 0px 55px 5px rgba(0,0,0,0.05)" };

  return (
    <Card sx={cardSx}>
      <Box sx={{ px: 2.5, pt: 2, pb: 1.5, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {isNewTheme && (
            <Box sx={{ width: 32, height: 32, borderRadius: "8px", bgcolor: `${accentColor}1a`, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <School sx={{ fontSize: 18, color: accentColor }} />
            </Box>
          )}
          <Box>
            <Typography variant="h6" fontWeight={isNewTheme ? 700 : 600} color={isNewTheme ? "text.primary" : "black"}>
              Top {mode === "universities" ? "Universities" : "Programs"}
            </Typography>
            {isNewTheme && <Box sx={{ width: 32, height: 3, borderRadius: 999, bgcolor: accentColor, mt: 0.5 }} />}
          </Box>
        </Box>
        <Stack direction="row" alignItems="center" spacing={1}>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={mode}
            onChange={(_, v) => v && setMode(v)}
            sx={{ "& .MuiToggleButton-root": { py: 0.25, px: 0.75, fontSize: "0.65rem", textTransform: "none", fontWeight: 600 } }}
          >
            <ToggleButton value="universities">Uni</ToggleButton>
            <ToggleButton value="programs">Program</ToggleButton>
          </ToggleButtonGroup>
          <Link to="/students" style={{ textDecoration: "none" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.3, color: "primary.main", fontSize: "0.75rem", fontWeight: 600, opacity: 0.85, "&:hover": { opacity: 1 } }}>
              <ArrowForward sx={{ fontSize: 13 }} />
            </Box>
          </Link>
        </Stack>
      </Box>

      <Divider sx={isNewTheme ? { borderColor: "#E2E8F0" } : {}} />

      <CardContent sx={{ px: 2.5, py: 2 }}>
        {ranking.length === 0 ? (
          <Typography variant="body2" color="text.disabled" sx={{ textAlign: "center", py: 2, fontStyle: "italic" }}>
            No data yet
          </Typography>
        ) : (
          <Stack spacing={1.25}>
            {ranking.map(([name, count], i) => {
              const pct = totalItems > 0 ? Math.round((count / totalItems) * 100) : 0;
              const barPct = (count / maxCount) * 100;
              const color = BAR_COLORS[i % BAR_COLORS.length];

              return (
                <Tooltip key={name} title={`${count} students · ${pct}% of top ${ranking.length}`} arrow>
                  <Box>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" mb={0.4}>
                      <Stack direction="row" alignItems="center" spacing={0.75} sx={{ minWidth: 0, flex: 1 }}>
                        <Typography
                          variant="caption"
                          fontWeight={600}
                          sx={{
                            color: "#0F172A",
                            fontSize: "0.72rem",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            maxWidth: "70%",
                          }}
                        >
                          {i + 1}. {name}
                        </Typography>
                      </Stack>
                      <Stack direction="row" alignItems="center" spacing={0.5} flexShrink={0}>
                        <Typography variant="caption" fontWeight={700} sx={{ color, fontSize: "0.72rem" }}>
                          {count}
                        </Typography>
                        <Typography variant="caption" color="text.disabled" sx={{ fontSize: "0.65rem" }}>
                          {pct}%
                        </Typography>
                      </Stack>
                    </Stack>
                    <Box sx={{ height: 6, borderRadius: 999, bgcolor: "#F1F5F9", overflow: "hidden" }}>
                      <Box
                        sx={{
                          height: "100%",
                          width: `${barPct}%`,
                          bgcolor: color,
                          borderRadius: 999,
                          transition: "width 0.5s ease",
                        }}
                      />
                    </Box>
                  </Box>
                </Tooltip>
              );
            })}
          </Stack>
        )}
      </CardContent>
    </Card>
  );
}

export default TopUniversitiesWidget;
